import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, TEST_API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from "utils/tokenManager";

// Components
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";
// import usePreventZoom from "hooks/usePreventZoom";
import usePreventInputScroll from "hooks/usePreventInputScroll";
import usePreventHorizontalScroll from "hooks/usePreventHorizontalScroll";

// CSS & Icons
import 'styles/Font.css';
import defaultCoachImg from 'assets/images/coachSample/coach_ex1.svg';
import calendarIcon from 'assets/images/icon/calendar-icon.svg';
import clockIcon from 'assets/images/icon/clock-icon.svg';
import dotIcon from 'assets/images/icon/dot-icon.svg';
import arrowRightIcon from 'assets/images/icon/arrow-right-icon.svg';

// [날짜 파싱 헬퍼 함수]
const parseDateTime = (dateStr, timeStr) => {
    try {
        // dateStr: "2025-12-05", timeStr: "15:00" (예시)
        // YYYY-MM-DD HH:MM 형식으로 합쳐서 Date 객체 생성
        // (만약 timeStr에 '오전/오후'가 있다면 변환 로직 필요, 여기서는 표준 포맷 가정)
        return new Date(`${dateStr} ${timeStr}`);
    } catch (e) {
        return new Date(); // 에러 방지용
    }
};

// [이름 마스킹 헬퍼 함수]
const maskName = (name) => {
    if (!name) return '';
    const len = name.length;
    if (len <= 1) return name;
    if (len === 2) {
        return name[0] + '*';
    }
    return name[0] + '*'.repeat(len - 2) + name[len - 1];
};

// 예약 카드 컴포넌트 (취소 버튼 조건부 표시)
const ReservationCard = ({ item, onCancel, isFeatured, onViewMembers }) => {
    // 현재 시간 구하기
    const now = new Date();

    // 취소 가능 여부 판단
    // - 미래의 예약인가? (item.dateTimeObj > now)
    // - 상태가 유효한가? (취소됨/수강완료가 아님)
    const isFuture = item.dateTimeObj > now;
    const isValidStatus = item.status !== '취소됨' && item.status !== '수강완료';

    const showCancelButton = isFuture && isValidStatus;

    // 상태에 따른 텍스트 색상 (선택사항)
    const statusColor = item.status === '취소됨' ? '#FF5252' : (item.status === '수강완료' ? '#747474' : '#FCFCFA');
    // 타입에 따른 뱃지 색상 다르게 주기 (선택사항)
    /* 기존 코드 주석 유지
    const badgeColor = item.stKind === 'E' ? '#8052FF' :
        item.stKind === 'G' ? '#FF5252' : // IPT: 빨강 계열
            item.stKind === 'F' ? '#2196F3' : // LYLO: 파랑 계열
                '#00C853'; // PT: 초록
    */
    const badgeColor = item.stKind === 'E' ? '#8052FF' : // 슬릭: 보라 계열
        item.stKind === 'G' ? '#FF5252' : // IPT: 빨강 계열
            item.stKind === 'F' ? '#2196F3' : // LYLO: 파랑 계열
                item.stKind === 'B' ? '#FFA500' : // OT: 주황 계열
                    '#00C853'; // PT 및 기본값: 초록

    return (
        <div style={styles.card}>
            <div style={styles.cardHeader}>
                <div style={styles.dateTimeWrapper}>
                    <img src={calendarIcon} alt="날짜" style={styles.icon} />
                    <span style={styles.dateText}>{item.date}</span>
                    <img src={dotIcon} alt="." style={styles.dotDivider} />
                    <img src={clockIcon} alt="시간" style={styles.icon} />
                    <span style={styles.timeText}>{item.time}</span>
                </div>

                <div style={styles.buttonGroup}>
                    {showCancelButton ? (
                        <button style={styles.cancelButton} onClick={() => onCancel(item)}>
                            예약취소
                        </button>
                    ) : (
                        <span style={{ ...styles.statusText, color: statusColor }}>
                            {item.status === '예약확정' && !isFuture ? '이용완료' : item.status}
                        </span>
                    )}
                </div>
            </div>
            <div style={styles.cardBody}>
                <img src={item.img || defaultCoachImg} alt="코치" style={styles.coachImg} />
                <div style={styles.infoBox}>
                    <span style={styles.coachName}>{item.coachName}</span>
                    {/* [수정] 지점명과 타입을 텍스트로 함께 표시 */}
                    <div style={styles.detailRow}>
                        <span style={styles.centerName}>{item.centerName || '지점명'}</span>
                        <span style={styles.textDivider}>|</span>
                        {/* 텍스트 색상을 뱃지 색상과 통일하여 강조 */}
                        <span style={{ ...styles.typeText, color: badgeColor }}>
                            {item.typeLabel} 수업
                        </span>
                        {isFeatured && isValidStatus && (
                            <button style={styles.memberButton} onClick={() => onViewMembers(item)}>
                                수강 인원
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// 수강 인원 바텀시트 컴포넌트
const MemberBottomSheet = ({ isOpen, onClose, isLoading, members, classInfo }) => {
    const [animateShow, setAnimateShow] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // 브라우저 렌더 큐 처리를 위해 약간의 지연 후 애니메이션 시작
            const timer = setTimeout(() => setAnimateShow(true), 50);
            return () => clearTimeout(timer);
        } else {
            setAnimateShow(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return createPortal(
        <div style={{ ...styles.sheetOverlay, ...(animateShow ? styles.sheetOverlayActive : {}) }} onClick={onClose}>
            <div style={{ ...styles.sheetContent, ...(animateShow ? styles.sheetContentActive : {}) }} onClick={(e) => e.stopPropagation()}>
                <div style={styles.sheetHeader}>
                    <div style={styles.sheetHeaderTitleBox}>
                        <h2 style={styles.sheetTitle}>수강 인원 목록</h2>
                        {classInfo && (
                            <span style={styles.sheetSubTitle}>
                                {classInfo.date} {classInfo.time} | {classInfo.coachName} 코치
                            </span>
                        )}
                    </div>
                    <button style={styles.sheetCloseButton} onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18" stroke="#FCFCFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6 6L18 18" stroke="#FCFCFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>

                <div style={styles.memberListContainer}>
                    {isLoading ? (
                        <div style={styles.memberEmptyState}>불러오는 중...</div>
                    ) : members.length > 0 ? (
                        <div style={styles.memberGrid}>
                            {members.map((name, index) => (
                                <div key={index} style={styles.memberCard}>
                                    <span style={styles.memberName}>{maskName(name)}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={styles.memberEmptyState}>현재 예약된 수강생이 없습니다.</div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

function MyReservations() {
    // usePreventZoom();
    usePreventInputScroll();
    usePreventHorizontalScroll();
    // const navigate = useNavigate();

    const [reservations, setReservations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);

    // 수강 인원 확인 바텀시트 상태
    const [memberBottomSheetOpen, setMemberBottomSheetOpen] = useState(false);
    const [reservedMembers, setReservedMembers] = useState([]);
    const [isMembersLoading, setIsMembersLoading] = useState(false);
    const [selectedClassInfo, setSelectedClassInfo] = useState(null);

    // [요구사항 4] 정렬 순서 상태 ('desc': 최신순/먼날짜, 'asc': 오래된순/가까운날짜)
    const [sortOrder, setSortOrder] = useState('desc');

    // 헤더 높이 측정을 위한 Ref와 State
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // --- [API] 수강생 목록 조회 ---
    const fetchReservedMembers = async (stKey) => {
        setIsMembersLoading(true);
        try {
            const { token } = getTokenStatus();
            const response = await fetch(`${API_BASE_URL}/reservedMembers?stKey=${stKey}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error("Network response was not ok");

            const resData = await response.json();
            const data = resData.data || resData;
            if (data.code === 200) {
                setReservedMembers(data.list || []);
            } else {
                setReservedMembers([]);
            }
        } catch (e) {
            console.error("Fetch Members Error:", e);
            setReservedMembers([]);
        } finally {
            setIsMembersLoading(false);
        }
    };

    const handleViewMembersClick = (item) => {
        setSelectedClassInfo({
            name: item.className || item.typeLabel + ' 수업',
            date: item.date,
            time: item.time,
            coachName: item.coachName
        });
        setReservedMembers([]);
        setMemberBottomSheetOpen(true);
        if (item.stKey) {
            fetchReservedMembers(item.stKey);
        }
    };

    // --- [API] 예약 목록 조회 ---
    const fetchReservations = async () => {
        setIsLoading(true);
        try {
            const { token } = getTokenStatus();

            // const response = await fetch(`${API_BASE_URL}/reserve`, {
            const response = await fetch(`${API_BASE_URL}/reserve`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error("Network response was not ok");

            const resData = await response.json();
            const data = resData.data || resData;

            let list = data.list || [];
            if (!Array.isArray(list)) list = [list];

            setReservations(list.map(item => {
                // stKind에 따른 타입 텍스트 결정
                /* 기존 코드 주석 유지
                let typeLabel = '예약'; // 기본값
                if (item.stKind === 'E') typeLabel = '부스트';
                else if (item.stKind === 'G') typeLabel = 'IPT';
                else if (item.stKind === 'F') typeLabel = 'LYLO';
                else if (item.stKind === 'C') typeLabel = 'PT';
                */
                let typeLabel = '예약'; // 기본값
                if (item.stKind === 'B') typeLabel = 'OT';
                else if (item.stKind === 'C') typeLabel = 'PT';
                else if (item.stKind === 'E') typeLabel = '슬릭';
                else if (item.stKind === 'F') typeLabel = 'LYLO';
                else if (item.stKind === 'G') typeLabel = 'IPT';

                return {
                    id: item.shKey,
                    stKey: item.stKey, // stKey 매핑 추가
                    stKind: item.stKind,    // 뱃지 색상 결정용
                    typeLabel: typeLabel,   // 텍스트 표시용 ('부스트' or 'PT')
                    date: item.date, // 예: '2025-12-05'
                    time: item.time,
                    coachName: item.name || '담당 코치',
                    className: item.name || '수업명',
                    img: item.img || defaultCoachImg,
                    status: item.status || '예약확정', // API에 status가 없다면 기본값
                    centerName: item.brName,
                    // 정렬용 Date 객체 추가
                    dateTimeObj: parseDateTime(item.date, item.time)
                };
            }));

        } catch (e) {
            console.error("Fetch Error:", e);
            setReservations([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReservations();
    }, []);

    // --- [API] 예약 취소 확정 ---
    const handleConfirmCancel = async () => {
        if (!selectedReservation) return;

        // [핵심 수정] stKey가 없으면 id(shKey)를 사용하도록 Fallback 처리
        // 서버가 stKey라는 파라미터 명을 원하지만, 값은 예약키(shKey)일 수도 있음
        const cancelKey = selectedReservation.shKey || selectedReservation.id;
        const { token } = getTokenStatus();

        const payload = {
            shKey: cancelKey // 수정된 키 전송
        }
        try {

            const response = await fetch(`${API_BASE_URL}/delReserve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`
                },
                body: new URLSearchParams(payload)
            });

            const data = await response.json();
            const resData = data.data || data;

            const msg = resData.message || resData.massage || "";

            // 성공 조건 (code 확인 또는 status 확인)
            if (resData.code === 200 || resData.code === '200' || resData.code === '0000'
            ) {
                alert(`예약이 취소되었습니다.`);
                fetchReservations();
            } else {
                alert(`취소 실패: ${msg}`);
            }
        } catch
        (e) {
            console.error(e);
            alert("취소 중 오류가 발생했습니다.");
        } finally {
            setCancelModalOpen(false);
            setSelectedReservation(null);
        }
    };


    const handleCancelClick = (item) => {
        setSelectedReservation(item);
        setCancelModalOpen(true);
    };

    // ▼▼▼ [요구사항 2, 3] 데이터 가공 및 정렬 로직 ▼▼▼
    const now = new Date();

    const { featuredReservation, sortedList } = useMemo(() => {
        if (reservations.length === 0) return { featuredReservation: null, sortedList: [] };

        // 미래에 있는 예약들만 골라내서 오름차순(가까운 순) 정렬 (취소된 것 제외)
        const upcomingList = reservations
            .filter(r => r.dateTimeObj >= now && r.status !== '취소됨')
            .sort((a, b) => a.dateTimeObj - b.dateTimeObj);

        // 가장 가까운 예약 1개 추출
        const featured = upcomingList.length > 0 ? upcomingList[0] : null;

        // 나머지는 전체 목록에서 featured를 뺀 것
        const others = reservations.filter(r => r.id !== featured?.id);

        // 나머지 목록 정렬 (sortOrder 적용)
        others.sort((a, b) => {
            if (sortOrder === 'desc') {
                return b.dateTimeObj - a.dateTimeObj; // 최신순
            } else {
                return a.dateTimeObj - b.dateTimeObj; // 오래된순
            }
        });

        return { featuredReservation: featured, sortedList: others };

    }, [reservations, sortOrder]);
    // ▲▲▲ ----------------------------------------- ▲▲▲

    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        // 초기 측정
        measureHeight();

        // 리사이즈 이벤트 대응 (Debounce 적용 권장하지만 간단히 requestAnimationFrame 사용)
        let animationFrame;
        const handleResize = () => {
            cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(measureHeight);
        };

        window.addEventListener('resize', handleResize);

        // featuredReservation 데이터가 변경될 때도 높이가 바뀔 수 있으므로 의존성 추가
        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrame);
        };
    }, [featuredReservation]); // featuredReservation이 바뀌면 헤더 내용이 바뀌므로 재측정


    // 메인 콘텐츠 동적 스타일
    const contentStyle = {
        ...styles.content,
        marginTop: `${headerHeight}px`, // 동적으로 계산된 높이 적용
    };

    return (
        <div style={styles.container}>
            {/*<div className="statusBar"/>
            <TopNavigator title="예약 내역"/>*/}
            <div style={styles.header} ref={headerRef}>
                <div className="statusBar" style={{ height: 'env(safe-area-inset-top, 0px)' }} />
                <TopNavigator title="예약 내역" />
            </div>

            <main style={contentStyle}>

                {isLoading ? (
                    <div style={styles.emptyState}>로딩 중...</div>
                ) : (
                    <>
                        {/* [요구사항 2] 가장 가까운 예약 단독 카드 */}
                        {featuredReservation && (
                            <div style={styles.featuredSection}>
                                <p style={styles.sectionTitle}>다가오는 예약</p>
                                <ReservationCard
                                    item={featuredReservation}
                                    isFuture={true}
                                    isFeatured={true}
                                    onCancel={handleCancelClick}
                                    onViewMembers={handleViewMembersClick}
                                />
                            </div>
                        )}

                        {/* 구분선 (메인 예약이 있을 때만) */}
                        {featuredReservation && <div style={styles.divider} />}

                        {/* [요구사항 3] 나머지 예약 정보 + 정렬 버튼 */}
                        <div style={styles.listSection}>
                            <div style={styles.filterBar}>
                                <span style={styles.sectionTitle}>
                                    {featuredReservation ? '전체 예약 내역' : '전체 예약 내역'}
                                </span>

                                {/* [요구사항 4] 정렬 변경 버튼 */}
                                <button
                                    style={styles.sortButton}
                                    onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                >
                                    {sortOrder === 'desc' ? '최신순' : '오래된순'}
                                    <img
                                        src={arrowRightIcon}
                                        alt="정렬"
                                        style={{
                                            ...styles.sortIcon,
                                            transform: 'rotate(90deg)' // 화살표 아래로 회전
                                        }}
                                    />
                                </button>
                            </div>

                            <div style={styles.listContainer}>
                                {sortedList.length > 0 ? (
                                    sortedList.map(res => (
                                        <ReservationCard
                                            key={res.id}
                                            item={res}
                                            onCancel={handleCancelClick}
                                        />
                                    ))
                                ) : (
                                    !featuredReservation && <div style={styles.emptyState}>예약된 내역이 없습니다.</div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </main>

            {/* 취소 확인 모달 */}
            <ConfirmModal
                isOpen={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
                onConfirm={handleConfirmCancel}
                title="예약 취소"
                message="정말 예약을 취소하시겠습니까?"
            />

            {/* 수강 인원 바텀시트 */}
            <MemberBottomSheet
                isOpen={memberBottomSheetOpen}
                onClose={() => setMemberBottomSheetOpen(false)}
                isLoading={isMembersLoading}
                members={reservedMembers}
                classInfo={selectedClassInfo}
            />
        </div>
    );
}

const styles = {
    container: {
        background: '#0D042D',
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        marginTop: '48px',
    },
    header: {
        position: 'fixed', top: 0, width: '100%', zIndex: 10, background: '#0D042D'
    },
    content: {
        flex: 1, overflowY: 'auto',
        // marginTop: '80px', // [삭제] 하드코딩 제거
        padding: '0 24px 24px 24px', display: 'flex', flexDirection: 'column', gap: '10px',
        paddingBottom: '50px'
    },
    emptyState: {
        color: '#9A9A9A', textAlign: 'center', marginTop: '50px', fontSize: '16px', fontFamily: 'Pretendard'
    },

    // 섹션 스타일
    featuredSection: {
        width: '100%',
    },
    sectionTitle: {
        fontFamily: 'Pretendard', fontWeight: '700', fontSize: '18px', color: '#FCFCFA',
        marginBottom: '12px', display: 'block'
    },
    divider: {
        width: '100%', height: '1px', backgroundColor: '#2C2A40', margin: '0'
    },

    // 정렬바 스타일
    filterBar: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'
    },
    sortButton: {
        background: 'none', border: 'none', color: '#9A9A9A', fontSize: '13px',
        fontFamily: 'Pretendard', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
    },
    sortIcon: { width: '10px', height: '10px', filter: 'invert(0.6)' }, // 회색톤

    // 카드 스타일
    card: {
        backgroundColor: '#1B1931', borderRadius: '12px', padding: '20px',
        display: 'flex', flexDirection: 'column', gap: '16px',
    },
    cardHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid #2C2A40', paddingBottom: '12px'
    },
    dateTimeWrapper: {
        display: 'flex', alignItems: 'center', gap: '6px'
    },
    dateText: {
        color: '#FFFFFF', fontFamily: 'Pretendard', fontWeight: '600', fontSize: '14px'
    },
    timeText: {
        color: '#FFFFFF', fontFamily: 'Pretendard', fontWeight: '600', fontSize: '14px'
    },
    icon: { width: '14px', height: '14px' },
    dotDivider: { width: '3px', height: '3px' },
    statusText: {
        fontFamily: 'Pretendard', fontWeight: '600', fontSize: '12px'
    },
    cancelButton: {
        background: 'transparent', border: '1px solid #747474', borderRadius: '4px',
        color: '#747474', padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
        fontFamily: 'Pretendard'
    },
    cardBody: {
        display: 'flex', alignItems: 'center', gap: '16px'
    },
    coachImg: {
        width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', backgroundColor: '#333'
    },
    infoBox: {
        display: 'flex', flexDirection: 'column', gap: '4px', flex: 1
    },
    coachName: {
        color: '#FCFCFA', fontSize: '16px', fontWeight: '600', fontFamily: 'Pretendard'
    },
    centerName: {
        color: '#9A9A9A', fontSize: '13px', fontFamily: 'Pretendard'
    },
    listContainer: {
        display: 'flex', flexDirection: 'column', gap: '16px'
    },

    // 상세 정보 행 (지점명 + 타입)
    detailRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        width: '100%',
    },
    // 구분선 (|)
    textDivider: {
        fontSize: '12px',
        color: '#555',
        marginTop: '-1px', // 시각적 보정
    },
    // 타입 텍스트 (부스트/PT)
    typeText: {
        fontFamily: 'Pretendard',
        fontWeight: '600',
        fontSize: '13px',
        // color는 인라인 스타일에서 동적으로 주입됨
    },
    buttonGroup: {
        display: 'flex', gap: '8px', alignItems: 'center'
    },
    memberButton: {
        background: 'transparent', border: '1px solid #8052FF', borderRadius: '4px',
        color: '#8052FF', padding: '4px 10px', fontSize: '12px', cursor: 'pointer',
        fontFamily: 'Pretendard', fontWeight: '600', marginLeft: 'auto'
    },
    // --- BottomSheet 관련 ---
    sheetOverlay: {
        position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1100,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        opacity: 0, transition: 'opacity 300ms ease-in-out',
        pointerEvents: 'none',
    },
    sheetOverlayActive: { opacity: 1, pointerEvents: 'auto' },
    sheetContent: {
        width: '100%', height: 'auto', maxHeight: '75vh', backgroundColor: '#1B1931', borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px', padding: '24px 24px calc(env(safe-area-inset-bottom, 34px) + 24px)', boxSizing: 'border-box', color: '#FCFCFA',
        boxShadow: '0px -4px 10px 0px rgba(0, 0, 0, 0.3)', transform: 'translateY(100%)',
        transition: 'transform 300ms cubic-bezier(0.25, 0.1, 0.25, 1)',
        display: 'flex', flexDirection: 'column',
    },
    sheetContentActive: { transform: 'translateY(0)' },
    sheetHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        marginBottom: '20px', flexShrink: 0,
    },
    sheetHeaderTitleBox: {
        display: 'flex', flexDirection: 'column', gap: '4px'
    },
    sheetTitle: {
        fontFamily: 'Pretendard', fontWeight: 700, fontSize: '20px',
        color: '#FCFCFA', margin: 0,
    },
    sheetSubTitle: {
        fontFamily: 'Pretendard', fontSize: '13px', color: '#9A9A9A',
    },
    sheetCloseButton: {
        background: 'transparent', border: 'none', cursor: 'pointer',
        padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    memberListContainer: {
        flex: 1, overflowY: 'auto', padding: '8px 0', minHeight: '150px'
    },
    memberGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', width: '100%'
    },
    memberCard: {
        backgroundColor: '#262446', borderRadius: '12px', padding: '16px 8px',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    memberName: {
        color: '#FCFCFA', fontSize: '14px', fontWeight: '600', fontFamily: 'Pretendard'
    },
    memberEmptyState: {
        color: '#9A9A9A', textAlign: 'center', marginTop: '40px', fontSize: '14px', fontFamily: 'Pretendard'
    }
};

export default MyReservations;